"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import MeasurementRecordDialog from "@/components/patient/measurements/record-dialog";
import Button from "@/components/ui/button";
import { useLocationClock } from "@/hooks/use-clinic-clock";
import { formatDate } from "@/lib/format-data";
import { formatMeasurementValue } from "@/lib/measurement-calculations";
import { deduplicateValuesByDate, groupValuesByDate, resolveGroupMemberships, sortedDates } from "@/lib/measurement-utils";
import { cx } from "@/lib/tailwind-utils";
import type { AppointmentChecklistItem, EventDetail } from "@/types/event";
import type { MeasurementField, MeasurementGroupWithFields, MeasurementValueSummary } from "@/types/measurement";
import { appointmentDayLabel } from "./appointment-day-label";
import { usePatientMeasurementData } from "@/query/patient/measurements";
function buildSyntheticGroup(field: MeasurementField): MeasurementGroupWithFields {
  return {
    id: "",
    name: field.name,
    sortOrder: 0,
    createdDate: "",
    isArchived: false,
    fieldMemberships: [{
      id: "",
      groupId: "",
      fieldId: field.id,
      childGroupId: null,
      sortOrder: 0,
      isRequired: false,
      field,
      childGroup: null
    }]
  };
}
function getDisplayableGroupFields(group: MeasurementGroupWithFields): MeasurementField[] {
  return resolveGroupMemberships(group, {
    includeSectionHeaders: false
  }).flatMap(item => item.type === "field" ? [item.field] : []).filter(field => !field.isArchived);
}
function getRecordableFieldCount(group: MeasurementGroupWithFields): number {
  return resolveGroupMemberships(group, {
    includeSectionHeaders: false,
    excludeDerived: true
  }).filter(item => item.type === "field" && !item.field.isArchived).length;
}
function MeasurementValueRow(t0) {
  const $ = _c(13);
  const {
    label,
    value,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cx("flex justify-between gap-3 text-caption", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="min-w-0 truncate text-foreground-secondary">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = value ? "text-foreground-secondary" : "text-foreground-muted";
  let t4;
  if ($[4] !== t3) {
    t4 = cx("shrink-0 whitespace-nowrap", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = value ?? "Not measured";
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t6) {
    t7 = <p className={t1}>{t2}{t6}</p>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
interface MeasurementChecklistActionProps {
  item: AppointmentChecklistItem;
  event: EventDetail;
  patientId: string;
  canUpdateItem: boolean;
}
export default function MeasurementChecklistAction(t0) {
  const $ = _c(78);
  const {
    item,
    event,
    patientId,
    canUpdateItem
  } = t0;
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  let t1;
  if ($[0] !== event.start) {
    t1 = new Date(event.start);
    $[0] = event.start;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const eventDate = t1;
  const eventClock = useLocationClock(event.location?.id);
  const {
    groups,
    fields,
    values,
    isLoading
  } = usePatientMeasurementData(patientId);
  let t2;
  if ($[2] !== fields) {
    t2 = new Map(fields.map(_temp));
    $[2] = fields;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const fieldById = t2;
  let t3;
  if ($[4] !== fieldById || $[5] !== item.measurementFieldId) {
    t3 = item.measurementFieldId ? fieldById.get(item.measurementFieldId) ?? null : null;
    $[4] = fieldById;
    $[5] = item.measurementFieldId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const targetField = t3;
  let t4;
  if ($[7] !== groups || $[8] !== item.measurementGroupId) {
    t4 = item.measurementGroupId ? groups.find(group => group.id === item.measurementGroupId) ?? null : null;
    $[7] = groups;
    $[8] = item.measurementGroupId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const targetGroup = t4;
  let t5;
  bb0: {
    if (targetField) {
      let t6;
      if ($[10] !== targetField) {
        t6 = [targetField];
        $[10] = targetField;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      t5 = t6;
      break bb0;
    }
    if (targetGroup) {
      let t6;
      if ($[12] !== targetGroup) {
        t6 = getDisplayableGroupFields(targetGroup);
        $[12] = targetGroup;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = [];
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = t6;
  }
  const targetFields = t5;
  let t6;
  bb1: {
    if (targetGroup) {
      t6 = targetGroup;
      break bb1;
    }
    if (targetField && !targetField.isDerived) {
      let t7;
      if ($[15] !== targetField) {
        t7 = buildSyntheticGroup(targetField);
        $[15] = targetField;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      t6 = t7;
      break bb1;
    }
    t6 = null;
  }
  const recordGroup = t6;
  let t7;
  if ($[17] !== recordGroup) {
    t7 = recordGroup ? getRecordableFieldCount(recordGroup) : 0;
    $[17] = recordGroup;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const recordableFieldCount = t7;
  let t8;
  if ($[19] !== eventClock || $[20] !== eventDate) {
    t8 = eventClock.dayOf(eventDate);
    $[19] = eventClock;
    $[20] = eventDate;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const eventDayKey = t8;
  let t9;
  if ($[22] !== eventClock.timeZone || $[23] !== values) {
    t9 = deduplicateValuesByDate(groupValuesByDate(values, eventClock.timeZone));
    $[22] = eventClock.timeZone;
    $[23] = values;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const valuesByDate = t9;
  let t10;
  if ($[25] !== eventDayKey || $[26] !== valuesByDate) {
    t10 = new Map((valuesByDate[eventDayKey] ?? []).map(_temp2));
    $[25] = eventDayKey;
    $[26] = valuesByDate;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const eventDayValueByFieldId = t10;
  let latest;
  if ($[28] !== valuesByDate) {
    latest = new Map();
    for (const date of sortedDates(valuesByDate)) {
      for (const value_0 of valuesByDate[date]) {
        if (!latest.has(value_0.fieldId)) {
          latest.set(value_0.fieldId, value_0);
        }
      }
    }
    $[28] = valuesByDate;
    $[29] = latest;
  } else {
    latest = $[29];
  }
  const latestValueByFieldId = latest;
  let t11;
  if ($[30] !== eventDayValueByFieldId || $[31] !== targetFields) {
    let t12;
    if ($[33] !== eventDayValueByFieldId) {
      t12 = field_0 => {
        const value_1 = eventDayValueByFieldId.get(field_0.id);
        return {
          field: field_0,
          value: value_1 ? formatMeasurementValue(value_1, field_0) : null
        };
      };
      $[33] = eventDayValueByFieldId;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t11 = targetFields.map(t12);
    $[30] = eventDayValueByFieldId;
    $[31] = targetFields;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const eventDayRows = t11;
  let t12;
  if ($[35] !== eventClock || $[36] !== latestValueByFieldId || $[37] !== targetFields) {
    let t13;
    if ($[39] !== eventClock || $[40] !== latestValueByFieldId) {
      t13 = field_1 => {
        const value_2 = latestValueByFieldId.get(field_1.id);
        if (!value_2) {
          return [];
        }
        return [{
          field: field_1,
          value: `${formatMeasurementValue(value_2, field_1)} (${formatDate(value_2.measurementDate, {
            month: "short",
            day: "numeric",
            timeZone: eventClock.timeZone
          })})`
        }];
      };
      $[39] = eventClock;
      $[40] = latestValueByFieldId;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    t12 = targetFields.flatMap(t13);
    $[35] = eventClock;
    $[36] = latestValueByFieldId;
    $[37] = targetFields;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const latestRows = t12;
  let t13;
  if ($[42] !== eventDayRows) {
    t13 = eventDayRows.some(_temp3);
    $[42] = eventDayRows;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const hasEventDayMeasurements = t13;
  let t14;
  if ($[44] !== event.start || $[45] !== eventClock) {
    t14 = appointmentDayLabel(event.start, eventClock);
    $[44] = event.start;
    $[45] = eventClock;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const appointmentDay = t14;
  const appointmentDateLabel = appointmentDay ?? "today";
  const title = appointmentDay ? `Measurements for ${appointmentDay}` : "Today's Measurements";
  const targetName = targetGroup?.name ?? targetField?.name ?? "measurement";
  const targetUnavailable = !targetField && !targetGroup && !isLoading;
  let t15;
  if ($[47] !== title) {
    t15 = <p className="text-caption font-medium text-foreground-secondary">{title}</p>;
    $[47] = title;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== appointmentDateLabel || $[50] !== eventDayRows || $[51] !== hasEventDayMeasurements || $[52] !== isLoading || $[53] !== latestRows || $[54] !== targetFields.length || $[55] !== targetUnavailable) {
    t16 = isLoading ? <p className="text-caption text-foreground-muted">Loading measurements...</p> : targetUnavailable ? <p className="text-caption text-foreground-muted">Measurement source no longer exists</p> : targetFields.length === 0 ? <p className="text-caption text-foreground-muted">No fields configured for this measurement source</p> : <>{hasEventDayMeasurements ? eventDayRows.map(_temp4) : <p className="text-caption text-foreground-muted">No measurements recorded on {appointmentDateLabel}.</p>}{!hasEventDayMeasurements && latestRows.length > 0 && <div className="mt-2 border-t border-border pt-2"><p className="text-caption font-medium text-foreground-secondary">Latest available measurements</p>{latestRows.map(_temp5)}</div>}</>;
    $[49] = appointmentDateLabel;
    $[50] = eventDayRows;
    $[51] = hasEventDayMeasurements;
    $[52] = isLoading;
    $[53] = latestRows;
    $[54] = targetFields.length;
    $[55] = targetUnavailable;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== t15 || $[58] !== t16) {
    t17 = <div className="my-1 flex flex-col rounded-md border px-3 py-2">{t15}{t16}</div>;
    $[57] = t15;
    $[58] = t16;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  let t18;
  if ($[60] !== canUpdateItem || $[61] !== item.completedDate || $[62] !== patientId || $[63] !== recordGroup || $[64] !== recordableFieldCount || $[65] !== targetName) {
    t18 = !item.completedDate && <div className="flex flex-col gap-2"><Button variant="secondary" size="sm" className="w-full" disabled={!canUpdateItem || !recordGroup || recordableFieldCount === 0 || !patientId} onClick={() => setRecordDialogOpen(true)}>Add {targetName}</Button>{!canUpdateItem && <p className="text-caption text-muted-foreground">You don't have permission to record this checklist measurement.</p>}</div>;
    $[60] = canUpdateItem;
    $[61] = item.completedDate;
    $[62] = patientId;
    $[63] = recordGroup;
    $[64] = recordableFieldCount;
    $[65] = targetName;
    $[66] = t18;
  } else {
    t18 = $[66];
  }
  let t19;
  if ($[67] !== event.id || $[68] !== eventClock || $[69] !== eventDayKey || $[70] !== patientId || $[71] !== recordDialogOpen || $[72] !== recordGroup) {
    t19 = recordGroup && <MeasurementRecordDialog isOpen={recordDialogOpen} onClose={() => setRecordDialogOpen(false)} group={recordGroup} patientId={patientId} eventId={event.id} clock={eventClock} defaultDay={eventDayKey} />;
    $[67] = event.id;
    $[68] = eventClock;
    $[69] = eventDayKey;
    $[70] = patientId;
    $[71] = recordDialogOpen;
    $[72] = recordGroup;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  let t20;
  if ($[74] !== t17 || $[75] !== t18 || $[76] !== t19) {
    t20 = <div className="flex flex-col">{t17}{t18}{t19}</div>;
    $[74] = t17;
    $[75] = t18;
    $[76] = t19;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  return t20;
}
function _temp5(t0) {
  const {
    field: field_3,
    value: value_4
  } = t0;
  return <MeasurementValueRow key={field_3.id} label={field_3.name} value={value_4} />;
}
function _temp4(t0) {
  const {
    field: field_2,
    value: value_3
  } = t0;
  return <MeasurementValueRow key={field_2.id} label={field_2.name} value={value_3} />;
}
function _temp3(row) {
  return row.value;
}
function _temp2(value) {
  return [value.fieldId, value];
}
function _temp(field) {
  return [field.id, field];
}